// Prod_US entry (/mobility-check): Welcome -> mobile number -> code -> Who is being checked -> About,
// then the person page (PersonDashboard, Metrics tab). A signed-in phone with a person goes
// straight to the person page. Screens and words: SeniorLife-Mobility-Prod_US-v1.0.html.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./prodUs.scss";
import heroImg from "../../assets/images/prodUs/hero.webp";
import logoImg from "../../assets/images/prodUs/logo.png";
import { openExternalUrl } from "../../utlis/webViewBridge";
import { addPatient, isDuplicateIdError } from "../mmtUserList/mmtApi";
import { getStoredSession } from "../mmtUserList/mmtFlow";
import {
  authErrorKey,
  currentUser,
  getIdToken,
  sendCode,
  verifyCode,
  whenAuthRestored,
} from "./firebasePhoneAuth";
import { getProdUsMe, saveWhoIsChecked } from "./prodUsApi";
import ProdUsAboutFields from "./ProdUsAboutFields";
import {
  keepTokenFresh,
  personPagePath,
  storeProdUsPerson,
  storeProdUsSession,
} from "./prodUsSession";
import { URLS } from "./prodUsConfig";
import {
  aboutErrors,
  formatPhone,
  maskPhone,
  toE164,
  toPersonSteps,
} from "./prodUsRules";
import { PROD_US_TEXT, pt } from "./prodUsStrings";

const RECAPTCHA_ID = "produs-recaptcha"; // invisible reCAPTCHA (Firebase phone sign-in) lives here
const MAX_ID_TRIES = 9;

const BackIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M19 12H5m0 0l7-7m-7 7l7 7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TermsLine = () => {
  const [before, rest] = PROD_US_TEXT.terms_line.split("{terms}");
  const [middle, after] = rest.split("{privacy}");
  const link = (url, label) => (
    <button
      type="button"
      className="linkbtn inline"
      onClick={() => openExternalUrl(url)}
    >
      {label}
    </button>
  );
  return (
    <p className="terms">
      {before}
      {link(URLS.terms, pt("terms"))}
      {middle}
      {link(URLS.privacy, pt("privacy"))}
      {after}
    </p>
  );
};

const AuthPage = ({ onBack, children }) => (
  <main className="produs auth">
    <div className="wrap">
      <header className="ahead">
        <button
          type="button"
          className="ab"
          onClick={onBack}
          aria-label={pt("back")}
        >
          <BackIcon />
        </button>
        <div className="brand">
          <img src={logoImg} alt="" />
          <span className="brand-t">
            <b>{pt("brand")}</b>
            <span>{pt("tagline")}</span>
          </span>
        </div>
      </header>
      {children}
    </div>
  </main>
);

const AppPage = ({ title, onBack, children }) => (
  <div className="produs app">
    <header className="hd">
      {onBack ? (
        <button
          type="button"
          className="ib"
          onClick={onBack}
          aria-label={pt("back")}
        >
          <BackIcon />
        </button>
      ) : null}
      <span className="hd-title">{title}</span>
    </header>
    <div className="body">{children}</div>
  </div>
);

const ProdUsGate = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState("loading"); // loading | welcome | phone | code | who | about
  const [phone, setPhone] = useState("");
  const [phoneErr, setPhoneErr] = useState("");
  const [code, setCode] = useState("");
  const [codeErr, setCodeErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [who, setWho] = useState(null); // "self" | "other"
  const [agreed, setAgreed] = useState(false);
  const [about, setAbout] = useState({
    fi: "",
    li: "",
    birthMonth: "",
    birthYear: "",
    feet: "",
    inches: "",
    sex: "",
  });
  const [tried, setTried] = useState(false);
  const [saveErr, setSaveErr] = useState("");
  const routed = useRef(false);

  // After sign-in (or on open, if already signed in): person page, or Who.
  const routeSignedIn = async (token) => {
    const me = await getProdUsMe();
    storeProdUsSession(token, me);
    if (me?.person) {
      routed.current = true;
      navigate(personPagePath(me.person), { replace: true });
    } else {
      setStep("who");
    }
  };

  useEffect(() => {
    keepTokenFresh();
    (async () => {
      try {
        await whenAuthRestored();
        // A signed-in phone goes straight to its person page, also when the app's
        // stored session is gone (a rejected token clears it, Firebase keeps the user).
        if (currentUser()) await routeSignedIn(await getIdToken());
        else setStep("welcome");
      } catch {
        setStep("welcome");
      }
    })();
  }, []);

  const onSend = async (event) => {
    event?.preventDefault();
    const e164 = toE164(phone);
    if (!e164) {
      setPhoneErr(pt("phone_req"));
      return;
    }
    setPhoneErr("");
    setBusy(true);
    try {
      await sendCode(e164, RECAPTCHA_ID);
      setCode("");
      setCodeErr("");
      setStep("code");
    } catch (error) {
      setPhoneErr(pt(authErrorKey(error)));
    } finally {
      setBusy(false);
    }
  };

  const onVerify = async (event) => {
    event?.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setCodeErr(pt("code_req"));
      return;
    }
    setBusy(true);
    try {
      const user = await verifyCode(code);
      await routeSignedIn(await user.getIdToken());
    } catch (error) {
      setCodeErr(
        pt(
          String(error?.code || "").startsWith("auth/")
            ? authErrorKey(error)
            : "try_again",
        ),
      );
    } finally {
      setBusy(false);
    }
  };

  const onResend = async () => {
    setCodeErr("");
    setBusy(true);
    try {
      await sendCode(toE164(phone), RECAPTCHA_ID);
      setCode("");
    } catch (error) {
      setCodeErr(pt(authErrorKey(error)));
    } finally {
      setBusy(false);
    }
  };

  const onAboutNext = async () => {
    setTried(true);
    if (Object.keys(aboutErrors(about)).length) return;
    setBusy(true);
    setSaveErr("");
    try {
      await saveWhoIsChecked(who);
      const session = getStoredSession();
      for (let attempt = 1; attempt <= MAX_ID_TRIES; attempt += 1) {
        const { step1, step2 } = toPersonSteps(about, attempt);
        try {
          await addPatient(session, step1, step2);
          break;
        } catch (error) {
          if (!isDuplicateIdError(error) || attempt === MAX_ID_TRIES)
            throw error;
        }
      }
      const me = await getProdUsMe();
      storeProdUsPerson(me.person);
      routed.current = true;
      navigate(personPagePath(me.person), { replace: true });
    } catch {
      setSaveErr(pt("save_failed"));
    } finally {
      setBusy(false);
    }
  };

  const openSample = URLS.sampleReport
    ? () => openExternalUrl(URLS.sampleReport)
    : null;

  const view = renderStep();
  return (
    <>
      {view}
      <div id={RECAPTCHA_ID} />
    </>
  );

  function renderStep() {
    if (step === "loading")
      return <main className="produs auth" aria-busy="true" />;

    if (step === "welcome") {
      return (
        <main className="produs auth welcome">
          <div className="wrap">
            <div className="brand">
              <img src={logoImg} alt="" />
              <span>{pt("brand")}</span>
            </div>
            <h1>{pt("product")}</h1>
            <span className="free">{pt("free_first")}</span>
            <img className="wel-img" src={heroImg} alt={pt("hero_alt")} />
            <p className="wnote">{pt("wnote")}</p>
            <button
              type="button"
              className="submit"
              onClick={() => setStep("phone")}
            >
              {pt("start_free")}
            </button>
            <div className="wel-links">
              {openSample ? (
                <button type="button" className="linkbtn" onClick={openSample}>
                  {pt("sample_btn")}
                </button>
              ) : null}
              <button
                type="button"
                className="linkbtn"
                onClick={() => setStep("phone")}
              >
                {pt("have_acct")}
              </button>
            </div>
          </div>
        </main>
      );
    }

    if (step === "phone") {
      return (
        <AuthPage onBack={() => setStep("welcome")}>
          <form className="card" onSubmit={onSend} noValidate>
            <div className="field">
              <label className="flabel" htmlFor="produs-phone">
                {pt("phone")}
              </label>
              <input
                id="produs-phone"
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                placeholder="(555) 555-5555"
                value={formatPhone(phone)}
                onChange={(e) =>
                  setPhone(e.target.value.replace(/\D/g, "").slice(0, 11))
                }
                aria-invalid={!!phoneErr}
                aria-describedby="produs-phone-err"
              />
              <p className="err" id="produs-phone-err" role="alert">
                {phoneErr}
              </p>
            </div>
            <button type="submit" className="submit" disabled={busy}>
              {busy ? pt("busy") : pt("send")}
            </button>
            {openSample ? (
              <button type="button" className="submit2" onClick={openSample}>
                {pt("sample_btn")}
              </button>
            ) : null}
            <TermsLine />
          </form>
        </AuthPage>
      );
    }

    if (step === "code") {
      const [pre, post] = PROD_US_TEXT.code_sent.split("{mask}");
      return (
        <AuthPage onBack={() => setStep("phone")}>
          <form className="card" onSubmit={onVerify} noValidate>
            <h2>{pt("code_title")}</h2>
            <p className="hint">
              {pre}
              <b>{maskPhone(toE164(phone))}</b>
              {post}
            </p>
            <div className="field">
              <label htmlFor="produs-code">{pt("code_label")}</label>
              <input
                id="produs-code"
                className="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                pattern="[0-9]*"
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                aria-invalid={!!codeErr}
                aria-describedby="produs-code-err"
              />
              <p className="err" id="produs-code-err" role="alert">
                {codeErr}
              </p>
            </div>
            <button type="submit" className="submit" disabled={busy}>
              {busy ? pt("busy") : pt("verify")}
            </button>
            <div className="row">
              <button
                type="button"
                className="linkbtn"
                onClick={onResend}
                disabled={busy}
              >
                {pt("resend")}
              </button>
              <button
                type="button"
                className="linkbtn"
                onClick={() => setStep("phone")}
              >
                {pt("change")}
              </button>
            </div>
          </form>
        </AuthPage>
      );
    }

    if (step === "who") {
      const ok = who === "self" || (who === "other" && agreed);
      return (
        <AppPage title={pt("who_title")}>
          <div className="opt">
            <button
              type="button"
              aria-pressed={who === "self"}
              onClick={() => setWho("self")}
            >
              {pt("who_me")}
            </button>
            <button
              type="button"
              aria-pressed={who === "other"}
              onClick={() => setWho("other")}
            >
              {pt("who_other")}
            </button>
          </div>
          {who === "other" ? (
            <label className="chk">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              <span>{pt("who_agreed")}</span>
            </label>
          ) : null}
          <p className="important">{pt("who_note")}</p>
          {openSample ? (
            <button type="button" className="obtn" onClick={openSample}>
              {pt("sample_btn")}
            </button>
          ) : null}
          <div className="spacer" />
          <button
            type="button"
            className="pbtn"
            disabled={!ok}
            onClick={() => setStep("about")}
          >
            {pt("next")}
          </button>
        </AppPage>
      );
    }

    // About
    const e = tried ? aboutErrors(about) : {};
    const err = (message) =>
      message ? (
        <p className="aerr" role="alert">
          {message}
        </p>
      ) : null;
    return (
      <AppPage
        title={pt(who === "other" ? "about_other" : "about_me")}
        onBack={() => setStep("who")}
      >
        <p className="sub">{pt("about_why")}</p>
        <ProdUsAboutFields about={about} setAbout={setAbout} errors={e} />
        {err(saveErr)}
        <div className="spacer" />
        <button
          type="button"
          className="pbtn"
          disabled={busy}
          onClick={onAboutNext}
        >
          {busy ? pt("busy") : pt("next")}
        </button>
      </AppPage>
    );
  }
};

export default ProdUsGate;
